import { Group } from 'three/webgpu';
import { EffectInstance } from './instance.js';
import { rendererStructure } from './layer.js';
import { applyWebGPUTextureViewCompat } from './webgpuCompat.js';
/**
 * Structural fingerprint of a def: the parts that force LayerRuntime/material
 * reconstruction, as opposed to parameters that hot-patch (curve shapes,
 * emitter numbers, delays). Used by updateDef() for the studio live-edit path.
 */
export function structuralSignature(def) {
    return JSON.stringify({
        space: def.meta.simulationSpace,
        layers: def.layers.map((l) => ({
            id: l.id,
            maxParticles: l.maxParticles,
            // only geometry/pool-affecting renderer params; shader/param edits hot-patch
            renderer: rendererStructure(l.renderer),
        })),
    });
}
export class EffectAsset {
    def;
    parent;
    maxInstances;
    baseSeed;
    spawnCounter = 0;
    active = [];
    free = [];
    constructor(def, parent, opts = {}) {
        this.def = def;
        this.parent = parent;
        this.maxInstances = Math.max(1, opts.maxInstances ?? 32);
        this.baseSeed = opts.seed ?? 0x51f0;
    }
    /** Fire the effect. Reuses a pooled instance; recycles the oldest when at cap. */
    spawn(opts = {}) {
        let inst = this.free.pop();
        if (!inst) {
            if (this.active.length >= this.maxInstances) {
                inst = this.active.shift();
            }
            else {
                inst = new EffectInstance(this.def, this.baseSeed);
                this.parent.add(inst.root);
            }
        }
        inst.activate(opts, opts.seed ?? this.baseSeed + this.spawnCounter++);
        this.active.push(inst);
        return inst;
    }
    update(dt) {
        for (let i = this.active.length - 1; i >= 0; i--) {
            const inst = this.active[i];
            inst.update(dt);
            if (inst.finished) {
                inst.deactivate();
                this.active.splice(i, 1);
                this.free.push(inst);
            }
        }
    }
    /**
     * Studio live-edit: swap the definition under running instances.
     * Returns 'patched' when instances kept playing (parameter/curve edits) or
     * 'rebuilt' when everything was torn down (structural edits — caller
     * respawns what it needs).
     */
    updateDef(def) {
        const structural = structuralSignature(def) !== structuralSignature(this.def);
        this.def = def;
        if (structural) {
            for (const inst of [...this.active, ...this.free])
                inst.dispose();
            this.active = [];
            this.free = [];
            return 'rebuilt';
        }
        for (const inst of [...this.active, ...this.free])
            inst.applyDef(def);
        return 'patched';
    }
    get activeInstances() {
        return this.active.length;
    }
    get aliveParticles() {
        let n = 0;
        for (const inst of this.active)
            n += inst.aliveParticles;
        return n;
    }
    dispose() {
        for (const inst of [...this.active, ...this.free])
            inst.dispose();
        this.active = [];
        this.free = [];
    }
}
/**
 * Entry point (plan §1.4):
 *
 *   const vfx = new VFXSystem(scene);
 *   const coinPop = vfx.register(coinPopEffect);
 *   coinPop.spawn({ position: [0, 1, 0] });
 *   // per frame:
 *   vfx.update(dt);
 */
export class VFXSystem {
    root = new Group();
    /** Global playback speed; the studio's slow-mo slider drives this. */
    timeScale = 1;
    assets = [];
    lastSimMs = 0;
    /**
     * `renderer` is accepted for forward compatibility (Phase 3 soft particles
     * need scene depth) but unused in Phase 1.
     */
    constructor(parent, _renderer) {
        applyWebGPUTextureViewCompat(); // three r185 swizzle bug on Chromium 141+ (see webgpuCompat.ts)
        this.root.name = 'VFXSystem';
        parent.add(this.root);
    }
    register(def, opts = {}) {
        const asset = new EffectAsset(def, this.root, opts);
        this.assets.push(asset);
        return asset;
    }
    unregister(asset) {
        asset.dispose();
        const i = this.assets.indexOf(asset);
        if (i >= 0)
            this.assets.splice(i, 1);
    }
    /** Call once per frame from the host app. */
    update(dt) {
        const start = performance.now();
        const d = Math.max(0, dt) * this.timeScale;
        for (const asset of this.assets)
            asset.update(d);
        this.lastSimMs = performance.now() - start;
    }
    stats() {
        let particles = 0;
        let instances = 0;
        for (const a of this.assets) {
            particles += a.aliveParticles;
            instances += a.activeInstances;
        }
        return { particles, instances, simMs: this.lastSimMs };
    }
    dispose() {
        for (const a of this.assets)
            a.dispose();
        this.assets = [];
        this.root.removeFromParent();
    }
}
